import { api } from '../index';
import type { WeeklyPlan } from './weekly-plans';

const PATIENTS_PATH = '/api/internal/patients';
const REQUIREMENTS_PATH = '/api/internal/planning/requirements';

// How often the visits recur. Only weekly requirements carry weekly plans; biweekly
// (quincenal) and monthly visits are scheduled by hand from the calendar.
export const REQUIREMENT_FREQUENCIES = ['weekly', 'biweekly', 'monthly'] as const;

export type RequirementFrequency = (typeof REQUIREMENT_FREQUENCIES)[number];

export type Requirement = {
  id: number;
  patientId: number;
  career: string;
  humanCareer: string;
  skillId: number | null;
  skillName: string | null;
  frequency: RequirementFrequency;
  humanFrequency: string;
  visitsPerPeriod: number;
  durationMin: number;
  startsOn: string;
  endsOn: string | null;
  humanStartsOn: string | null;
  humanEndsOn: string | null;
  createdAt: string;
  blockedCaregiverIds: number[];
  weeklyPlans: Array<WeeklyPlan>;
};

export interface RequirementForm {
  career: string;
  skillId?: number | null;
  frequency: RequirementFrequency;
  visitsPerPeriod: number;
  durationMin: number;
  startsOn: string;
  endsOn?: string | null;
}

export const requirementsApi = {
  index(patientId: number): Promise<Requirement[]> {
    return api({
      method: 'get',
      url: `${PATIENTS_PATH}/${patientId}/requirements`,
    }).then(response => response.data.requirements);
  },
  create(patientId: number, form: RequirementForm): Promise<Requirement> {
    return api({
      method: 'post',
      url: `${PATIENTS_PATH}/${patientId}/requirements`,
      data: { requirement: form },
    }).then(response => response.data.requirement);
  },
  update(id: number, form: Partial<RequirementForm>): Promise<Requirement> {
    return api({
      method: 'put',
      url: `${REQUIREMENTS_PATH}/${id}`,
      data: { requirement: form },
    }).then(response => response.data.requirement);
  },
  destroy(id: number): Promise<void> {
    return api({
      method: 'delete',
      url: `${REQUIREMENTS_PATH}/${id}`,
    }).then(() => {});
  },
};
